import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, RepeatIcon } from "@chakra-ui/icons";
import { usePartyNames } from "../../hooks/usePartyNames";
import { getSignedLineAdvicesForPlacement } from "../../services/PlacingAPI";
import BrokerSubmissionView from "./BrokerSubmissionView";
import SignDownPanel from "./SignDownPanel";
import SignedDownView from "./SignedDownView";
import { RoleBadge, StandsBadge } from "./LineBadges";
import { ReasonText } from "./ReasonFields";
import { formatDate, isSignedDown } from "./placingUtils";
import { carrierHistory, currentQuote, defaultStage, placementStages } from "./placementStages";

const raw = (v) => v?.$date ?? v;
const dayMonth = (v) => (raw(v) ? new Date(raw(v)).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" }) : "");
const hourMinute = (v) => (raw(v) ? new Date(raw(v)).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "");
const dayTime = (v) => (raw(v) ? `${dayMonth(v)} ${hourMinute(v)}` : "");
const initials = (name) =>
  (name || "")
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const PILL_COLOURS = { green: "green", red: "red", yellow: "yellow", gray: "gray", blue: "blue", orange: "orange" };

const Pill = ({ pill }) =>
  pill ? (
    <Badge colorScheme={PILL_COLOURS[pill.tone] || "gray"} borderRadius="full" px={2.5} py={0.5} textTransform="none" fontWeight="medium">
      {pill.label}
      {pill.date && <Text as="span" ml={1} fontWeight="normal">{hourMinute(pill.date)}</Text>}
    </Badge>
  ) : null;

const Figure = ({ label, value, big }) => (
  <Box minW="80px">
    <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wide" color="gray.500">{label}</Text>
    <Text fontFamily="mono" fontSize={big ? "xl" : "sm"} fontWeight={big ? "semibold" : "normal"} lineHeight="short">{value}</Text>
  </Box>
);

/** One of the four process blocks. */
const StageCard = ({ stage, isSelected, onSelect }) => {
  const complete = stage.state === "complete";
  const todo = stage.state === "todo";
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      onClick={onSelect}
      aria-pressed={isSelected}
      borderWidth={isSelected ? "2px" : "1px"}
      borderColor={isSelected ? "blue.600" : complete ? "green.200" : "gray.200"}
      bg={isSelected ? "white" : complete ? "green.50" : "whiteAlpha.800"}
      boxShadow={isSelected ? "md" : undefined}
      borderRadius="lg"
      p={4}
      opacity={todo && !isSelected ? 0.75 : 1}
      _hover={{ borderColor: isSelected ? "blue.600" : "blue.300" }}
      transition="border-color 0.15s">
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          {complete ? (
            <Flex boxSize="26px" borderRadius="full" bg="green.500" color="white" align="center" justify="center"><CheckIcon boxSize={3} /></Flex>
          ) : (
            <Box boxSize="26px" borderRadius="full" borderWidth="2px" borderColor={todo ? "gray.300" : "blue.500"} bg={todo ? undefined : "blue.50"} />
          )}
          <Heading size="sm">{stage.title}</Heading>
        </HStack>
        <Text fontSize="xs" fontWeight="semibold" color={complete ? "green.600" : todo ? "gray.400" : "blue.600"}>
          {complete ? "Complete" : todo ? "Not started" : "In progress"}
        </Text>
      </HStack>
      <Text fontSize="sm" color="gray.700">{stage.summary}</Text>
      <Text fontFamily="mono" fontSize="sm" mt={1}>{stage.figure}</Text>
    </Box>
  );
};

/** Order: each carrier's written line, as segments of a bar with the 100% order marked. */
const WrittenBar = ({ rows, total }) => {
  const scale = Math.max(100, total);
  return (
    <HStack spacing={3} flex="1" maxW="560px">
      <Box position="relative" flex="1" h="10px" bg="gray.100" borderRadius="full" overflow="hidden">
        <HStack spacing="1px" h="100%">
          {rows.filter((r) => r.share > 0).map((r, i) => (
            <Box key={r.partyId} h="100%" w={`${(r.share / scale) * 100}%`} bg={r.role === "lead" ? "blue.600" : i % 2 ? "teal.300" : "teal.500"} />
          ))}
        </HStack>
        {total > 100 && <Box position="absolute" top={0} bottom={0} left={`${(100 / scale) * 100}%`} w="2px" bg="gray.800" />}
      </Box>
      <Text fontFamily="mono" fontSize="sm" whiteSpace="nowrap">{total}% of 100% order</Text>
    </HStack>
  );
};

/** Signing: written against signed, as two bars. */
const SignedBars = ({ written, signed, scale }) => (
  <VStack spacing={1} flex="1" align="stretch">
    <Box h="6px" bg="gray.100" borderRadius="full"><Box h="100%" w={`${(written / scale) * 100}%`} bg="gray.300" borderRadius="full" /></Box>
    <Box h="6px" bg="gray.100" borderRadius="full"><Box h="100%" w={`${(signed / scale) * 100}%`} bg="green.500" borderRadius="full" /></Box>
  </VStack>
);

const CarrierCell = ({ name, sub, subTone, role }) => (
  <HStack spacing={3} minW={0}>
    <Avatar size="sm" name={name} getInitials={initials} bg="gray.100" color="gray.700" />
    <Box minW={0}>
      <HStack spacing={2}>
        <Text fontWeight="semibold" noOfLines={1}>{name}</Text>
        {role && <RoleBadge role={role} />}
      </HStack>
      {sub && <Text fontSize="xs" color={subTone || "gray.500"} noOfLines={2}>{sub}</Text>}
    </Box>
  </HStack>
);

const Row = ({ children, onClick, highlight }) => (
  <Grid
    templateColumns={{ base: "1fr", md: "minmax(220px, 1.3fr) 2fr auto" }}
    gap={3}
    alignItems="center"
    px={4}
    py={3}
    borderTopWidth="1px"
    bg={highlight ? "blue.50" : undefined}
    cursor="pointer"
    _hover={{ bg: highlight ? "blue.100" : "gray.50" }}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}>
    {children}
  </Grid>
);

/** The selected stage: one row per carrier. */
const StageDetail = ({ stage, names, onCarrier, onAction }) => {
  const signingScale = stage.key === "signing" ? Math.max(1, ...stage.rows.map((r) => r.written)) : 1;
  return (
    <Box borderWidth="1px" borderRadius="lg" bg="whiteAlpha.800">
      <HStack justify="space-between" px={4} py={3} spacing={4} flexWrap="wrap" rowGap={2}>
        <Box>
          <Heading size="sm">{stage.heading}</Heading>
          <Text fontSize="xs" color="gray.500">{stage.subtitle}</Text>
        </Box>
        {stage.key === "order" && stage.rows.length > 0 && <WrittenBar rows={stage.rows} total={stage.writtenTotal} />}
        <HStack>
          {stage.badge && <Badge colorScheme="green" borderRadius="full" px={3} py={1} textTransform="none">{stage.badge}</Badge>}
          {/* Once signed down everything is in the rows, so there's no more detail to open */}
          {!(stage.key === "signing" && stage.complete && !stage.action) && (
            <Button size="sm" variant={stage.action ? "solid" : "outline"} colorScheme={stage.action ? "blue" : undefined} onClick={onAction}>
              {stage.action || "Full detail"}
            </Button>
          )}
        </HStack>
      </HStack>

      {stage.rows.length === 0 && (
        <Text px={4} py={4} borderTopWidth="1px" fontSize="sm" color="gray.500">{stage.empty}</Text>
      )}

      {stage.rows.map((r) => (
        <Row key={r.partyId} onClick={() => onCarrier(r.partyId)} highlight={r.highlight}>
          <CarrierCell name={names[r.partyId] || "Loading…"} sub={r.sub} subTone={r.subTone} role={stage.key === "order" || stage.key === "signing" ? r.role : undefined} />

          {stage.key === "build" && (
            <HStack spacing={3} fontSize="sm" color="gray.600" flexWrap="wrap">
              {r.steps.map((step) => (
                <Fragment key={step.label}>
                  <Text>{step.label} {step.withDay ? dayTime(step.date) : hourMinute(step.date)}</Text>
                  <Text color="gray.400">→</Text>
                </Fragment>
              ))}
              <Pill pill={r.pill} />
            </HStack>
          )}

          {(stage.key === "quotation" || stage.key === "order") && (
            <HStack spacing={4} flexWrap="wrap">
              <Figure label={r.from.label} value={r.from.value} />
              <Text color="gray.400">→</Text>
              <Figure label={r.to?.label || (stage.key === "order" ? "Written" : "Quoted")} value={r.to?.value || "—"} big={!!r.to} />
              {r.extra && <Figure label={r.extra.label} value={`${dayTime(r.extra.date)}${r.extra.expired ? " · expired" : ""}`} />}
              {r.tag && <Badge colorScheme="purple" borderRadius="full" px={2.5} textTransform="none">{r.tag}</Badge>}
            </HStack>
          )}

          {stage.key === "signing" && (
            <HStack spacing={4}>
              <Figure label="Written" value={`${r.written}%`} />
              <Box w="64px">{r.stands && <StandsBadge />}</Box>
              <SignedBars written={r.written} signed={r.signed} scale={signingScale} />
              <Box textAlign="right"><Figure label="Signed" value={`${r.signed}%`} big /></Box>
            </HStack>
          )}

          <HStack justify="flex-end" spacing={3}>
            {stage.key !== "build" && <Pill pill={r.pill} />}
            {r.right && <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{r.right.label} {dayTime(r.right.date)}</Text>}
          </HStack>
        </Row>
      ))}

      {stage.totals && stage.rows.length > 0 && (
        <Grid templateColumns={{ base: "1fr", md: "minmax(220px, 1.3fr) 2fr auto" }} gap={3} px={4} py={3} borderTopWidth="1px" alignItems="center">
          <Text fontWeight="semibold">Total</Text>
          <HStack justify="space-between">
            <Text fontFamily="mono" fontWeight="semibold">{stage.totals.written}%</Text>
            <Text fontFamily="mono" fontWeight="semibold">{stage.totals.signed}%</Text>
          </HStack>
          <Box />
        </Grid>
      )}
    </Box>
  );
};

const TONE_COLOURS = { red: "red.500", green: "green.500", blue: "blue.500", orange: "orange.400", teal: "teal.500" };

/** Everything that's happened with one carrier on this placement. */
const CarrierHistory = ({ events }) =>
  events.length === 0 ? (
    <Text fontSize="sm" color="gray.500">Nothing yet.</Text>
  ) : (
    <VStack align="stretch" spacing={0}>
      {events.map((e, i) => (
        <HStack key={i} align="start" spacing={3} pb={4}>
          <VStack spacing={0} pt={1} alignSelf="stretch">
            <Box boxSize="10px" borderRadius="full" bg={TONE_COLOURS[e.tone] || "gray.400"} />
            {i < events.length - 1 && <Box flex="1" w="2px" bg="gray.200" mt={1} />}
          </VStack>
          <Box flex="1">
            <HStack justify="space-between" align="start">
              <Text fontSize="sm" fontWeight="medium">{e.text}</Text>
              <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{e.date ? formatDate(e.date) : ""}</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">{e.stage}</Text>
            {e.detail && (e.detail.code ? (
              <ReasonText fontSize="xs" color="gray.700" code={e.detail.code} notes={e.detail.notes} />
            ) : (
              e.detail.notes && <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">{e.detail.notes}</Text>
            ))}
          </Box>
        </HStack>
      ))}
    </VStack>
  );

/**
 * Broker: one placement in a single pane. Four process blocks (Build,
 * Quotation, Order, Signing); clicking one shows its carriers below. Each
 * carrier's full history, and each stage's forms, open in a side drawer.
 * data is what usePlacement returned for the placement.
 */
const PlacementOverview = ({ data, editorLink, showDealLink = true, onPausedChange }) => {
  const navigate = useNavigate();
  const { loading, error, placement, submission, comparison, lines, reload, refreshing, lastUpdated } = data;
  const [advices, setAdvices] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState(null); // { stage } or { partyId }
  const closed = isSignedDown(placement);

  // Auto-refresh waits while a stage's forms are open, so nothing changes mid-edit
  useEffect(() => {
    onPausedChange?.(!!drawer?.stage);
  }, [drawer, onPausedChange]);

  useEffect(() => {
    if (!closed || !placement?._id) return;
    getSignedLineAdvicesForPlacement(placement._id).then(setAdvices).catch(() => setAdvices([]));
  }, [closed, placement?._id]);

  const partyIds = useMemo(
    () => [
      ...(submission?.sharedWithParties || []).map((l) => l.partyId),
      ...(comparison?.rows || []).map((r) => r.partyId),
      ...(lines?.rows || []).map((r) => r.partyId),
      ...(advices || []).map((a) => a.receiver?.partyId),
    ],
    [submission, comparison, lines, advices]
  );
  const names = usePartyNames(partyIds);

  const stages = useMemo(
    () => placementStages({ placement, submission, comparison, lines, advices, names }),
    [placement, submission, comparison, lines, advices, names]
  );

  if (loading) return <Spinner />;
  if (error) return <Alert status="error"><AlertIcon />{error.message || "Couldn't load placement."}</Alert>;
  if (!placement) return <Alert status="warning"><AlertIcon />Placement not found.</Alert>;

  const stage = stages.find((s) => s.key === (selected || defaultStage(stages)));
  const current = stages.find((s) => s.state === "current");
  const respondBy = submission?.responseRequiredByDate;
  const nominatedQuote = currentQuote((comparison?.rows || []).filter((r) => r.status === "nominated").flatMap((r) => r.quotes || []));
  const currency = nominatedQuote?.currency || currentQuote((comparison?.rows || []).flatMap((r) => r.quotes || []))?.currency;
  const documentLink = placement.documentId && (editorLink ? editorLink(placement.documentId) : `/editor?doc=${placement.documentId}`);

  const closeDrawer = async () => {
    setDrawer(null);
    await reload(); // pick up whatever was done in the drawer
  };

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="start" flexWrap="wrap" rowGap={2}>
        <Box>
          <Heading size="lg">{placement.name || "Placement"}</Heading>
          <Text fontSize="sm" color="gray.600">
            {[
              placement.productCode && placement.productCode.toUpperCase(),
              currency,
              respondBy && `respond by ${new Date(raw(respondBy)).toLocaleDateString()}`,
            ].filter(Boolean).join(" · ")}
          </Text>
        </Box>
        <HStack spacing={4}>
          <HStack spacing={1}>
            <Tooltip label="Refreshes every minute while this page is open" hasArrow>
              <Text fontSize="xs" color="gray.500">
                Updated {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
              </Text>
            </Tooltip>
            <IconButton size="xs" variant="ghost" aria-label="Refresh now" icon={<RepeatIcon />} onClick={reload} isLoading={refreshing} />
          </HStack>
          {closed ? (
            <Badge colorScheme="green" borderRadius="full" px={3} py={1} textTransform="none">
              Signed down{placement.signedDownDate ? ` · ${dayTime(placement.signedDownDate)}` : ""}
            </Badge>
          ) : current ? (
            <Badge colorScheme="blue" borderRadius="full" px={3} py={1} textTransform="none">{current.title} in progress</Badge>
          ) : null}
          {documentLink && <Button size="sm" variant="link" colorScheme="blue" onClick={() => navigate(documentLink)}>Open document</Button>}
          {showDealLink && placement.dealId && placement.productCode && (
            <Button size="sm" variant="link" colorScheme="blue" onClick={() => navigate(`/product/${placement.productCode}/deal/${placement.dealId}/deal`)}>
              Open deal
            </Button>
          )}
        </HStack>
      </HStack>

      <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }} gap={3}>
        {stages.map((s) => (
          <StageCard key={s.key} stage={s} isSelected={s.key === stage.key} onSelect={() => setSelected(s.key)} />
        ))}
      </Grid>

      {!submission ? (
        <Alert status="info"><AlertIcon />This placement has no submission yet. Create it from the document in the editor.</Alert>
      ) : (
        <StageDetail stage={stage} names={names} onCarrier={(partyId) => setDrawer({ partyId })} onAction={() => setDrawer({ stage: stage.key })} />
      )}

      <Text fontSize="xs" color="gray.500" textAlign="center">
        Click a stage to see its detail. Click a carrier for their full history and timestamps.
      </Text>

      <Drawer isOpen={!!drawer} onClose={closeDrawer} placement="right" size="full">
        <DrawerOverlay />
        <DrawerContent maxW={{ base: "100vw", lg: "1100px" }} bg="gray.50">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" bg="white">
            {drawer?.partyId ? (
              <HStack spacing={3}>
                <Avatar size="sm" name={names[drawer.partyId]} getInitials={initials} bg="gray.100" color="gray.700" />
                <Box>
                  <Text>{names[drawer.partyId] || "Carrier"}</Text>
                  <Text fontSize="sm" fontWeight="normal" color="gray.500">History on {placement.name || "this placement"}</Text>
                </Box>
              </HStack>
            ) : (
              <Box>
                <Text>{stages.find((s) => s.key === drawer?.stage)?.heading}</Text>
                <Text fontSize="sm" fontWeight="normal" color="gray.500">{placement.name}</Text>
              </Box>
            )}
          </DrawerHeader>
          <DrawerBody py={5}>
            {drawer?.partyId && (
              <CarrierHistory events={carrierHistory({ partyId: drawer.partyId, submission, comparison, lines, advices })} />
            )}
            {drawer?.stage && submission && ["build", "quotation", "order"].includes(drawer.stage) && (
              <BrokerSubmissionView submission={submission} onChanged={reload} section={drawer.stage} />
            )}
            {drawer?.stage === "signing" &&
              (closed ? (
                <SignedDownView placement={placement} />
              ) : stages[3].action ? (
                <SignDownPanel placement={placement} lines={lines} comparison={comparison} onSigned={reload} />
              ) : (
                <Text fontSize="sm" color="gray.500">Nothing to sign yet: lines come in Order.</Text>
              ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
};

export default PlacementOverview;
